import * as yup from 'yup';

import { IProductAiImages } from '../../modules/pages/page-form/page-form';
import { Marketplaces } from '../constants/enums';

export const asinRegex = /[A-Z0-9]{10}/g;

export const validationMessages = {
  gender: 'Must be "Male" or "Female"',
  incorrect: 'Incorrect value',
  noSelectedImages: 'No selected images',
  notEmpty: "Can't be empty",
  onlyNumber: 'Should be number',
  required: 'Field is required',
};

export const createAuthorFormSchema = yup
  .object({
    first_name: yup.string().required(validationMessages.required).trim(),
    gender: yup
      .mixed()
      .oneOf(['male', 'female'], validationMessages.gender)
      .required(validationMessages.required),
    info: yup.object({
      about: yup.string().required(validationMessages.required).trim(),
      city: yup.string().required(validationMessages.required).trim(),
      country: yup.string().required(validationMessages.required).trim(),
      experience: yup.object({
        company: yup.string().trim().nullable(),
        specialization: yup.string().required(validationMessages.required).trim(),
        years: yup
          .number()
          .typeError(validationMessages.onlyNumber)
          .required(validationMessages.required),
      }),
      state: yup.string().required(validationMessages.required).trim(),
    }),
    last_name: yup.string().required(validationMessages.required).trim(),
  })
  .required();

export const amazonProductAsinValidationSchema = yup.object({
  product_id: yup
    .string()
    .required(validationMessages.required)
    .trim()
    .length(10, 'Should be 10 symbols'),
});

export const walmartProductIdValidationSchema = yup.object({
  product_id: yup
    .number()
    .typeError('Should be only numbers')
    .required(validationMessages.required)
});

export const marketplaceValidationSchema = yup
  .string()
  .oneOf([Marketplaces.Walmart, Marketplaces.Amazon], 'Must be one of: Amazon, Walmart')
  .required(validationMessages.required);

export const brandValidationSchema = yup.string().when('marketplace', {
  is: Marketplaces.Amazon,
  otherwise: (schema) => schema,
  then: (schema) => schema.required(validationMessages.required),
});

export const productContentValidationSchema = yup.array().of(
  yup.object({
    badge: yup.object({
      mainText: yup.object({
        text: yup.string().trim().required(validationMessages.required),
        style: yup.string()
          .oneOf(['orange', 'blue', 'green', 'gray', 'navy'])
          .required(validationMessages.required),
      }).required(),
      additionalText: yup.object({
        text: yup.string().trim().required(validationMessages.required),
        style: yup.string().oneOf(['plain', 'link']).required(validationMessages.required),
        url: yup.string().trim().nullable().test(
          'badge-url',
          'Use a root-relative or HTTP(S) URL',
          (value) => !value || value.startsWith('/') || /^https?:\/\//i.test(value),
        ),
      }).nullable().optional().default(undefined),
    }).nullable().optional().default(undefined),
    faq: yup.array().of(
      yup.object({
        answer: yup.string().trim().required(validationMessages.required),
        question: yup.string().trim().required(validationMessages.required),
      }),
    ),
  }),
);

export const productAiImageSchema = yup.object({
  regenerate: yup.boolean()
});

export const productAiImagesValidationSchema = yup.object({
  ids: yup.array()
    .of(productAiImageSchema)
    .test(
      'at-least-one-true',
      validationMessages.noSelectedImages,
      // eslint-disable-next-line func-names
      function (ids) {
        const { prompt } = this.parent;
        if (typeof prompt === 'string' && prompt.length > 0) {
          return ids.some((elem) => elem.regenerate);
        }
        return true;
      }
    ),
  prompt: yup.string().when('ids', {
    is: (ids: IProductAiImages['ids']) => ids.some(((elem) => elem.regenerate)),
    otherwise: (schema) => schema,
    then: (schema) => schema.required(validationMessages.required)
  })
});

export const pageFormValidationSchema = yup.object({
  content: productContentValidationSchema,
});

const productContentV2ValidationSchema = yup.object({
  faq: yup.array().of(
    yup.object({
      answer: yup.string().trim().required(validationMessages.required),
      question: yup.string().trim().required(validationMessages.required),
    }),
  ),
  pros: yup.array().of(yup.object({ value: yup.string().trim().required(validationMessages.required) })),
  summary: yup.string().trim(),
  bottomLine: yup.string().trim(),
});

export const pageBuilderV2ValidationSchema = yup.object({
  products: yup
    .array()
    .min(1, 'At least one product is required')
    .of(
      yup.object({
        content: productContentV2ValidationSchema,
      }),
    )
    .required(validationMessages.required),
  publisher: yup
    .object({ id: yup.number() })
    .nullable()
    .test('publisher-required', validationMessages.required, (value) => value !== null && value !== undefined),
  title: yup.string().required(validationMessages.required),
});

export const createTemporaryLinkValidateSchema = yup.object().shape({
  date: yup.date().required(validationMessages.required).min(new Date(), 'At least tomorrow'),
  publisher_id: yup.number().nullable(),
  tags: yup.array().of(
    yup.object().shape({
      itemID: yup
        .string()
        .required(validationMessages.required)
        .trim(),
      tag: yup.string().required(validationMessages.required).trim(),
    }),
  ),
});

export const dailyDealsFormValidateSchema = yup.object().shape({
  rows: yup.array().of(
    yup.object().shape({
      category: yup.object({
        code: yup.string().required(validationMessages.required)
      }).shape({
      }).required(validationMessages.required),
      products: yup
        .array()
        .min(1, validationMessages.notEmpty)
        .required(validationMessages.required),
    }),
  ),
});
